import {
  DailyDiscountStats,
  DailyMarketStats,
  ExtremeWithCount,
  OtaSummaryStats,
  ParsedOtaCsv,
  Quartiles,
} from './types';

/** Linear-interpolation percentile (Excel PERCENTILE.INC/QUARTILE.INC), ported from the mockup's percentile(). */
export function percentile(values: ReadonlyArray<number>, p: number): number | null {
  if (values.length === 0) {
    return null;
  }
  const sorted = [...values].sort((a, b) => a - b);
  if (sorted.length === 1) {
    return sorted[0];
  }
  const idx = (sorted.length - 1) * p;
  const lo = Math.floor(idx);
  const hi = Math.ceil(idx);
  if (lo === hi) {
    return sorted[lo];
  }
  return sorted[lo] + (sorted[hi] - sorted[lo]) * (idx - lo);
}

export function mean(values: ReadonlyArray<number>): number | null {
  return values.length ? values.reduce((sum, v) => sum + v, 0) / values.length : null;
}

function quartilesOf(values: ReadonlyArray<number>): Quartiles {
  return { q1: percentile(values, 0.25), median: percentile(values, 0.5), q3: percentile(values, 0.75) };
}

function allOtaQuoteValues(parsed: ParsedOtaCsv): Array<number> {
  const values: Array<number> = [];
  for (const row of parsed.rows) {
    for (const value of Object.values(row.otaRates)) {
      if (value != null) {
        values.push(value);
      }
    }
  }
  return values;
}

function extremeWithCount(
  values: ReadonlyArray<number>,
  pick: (a: number, b: number) => number,
): ExtremeWithCount | null {
  if (values.length === 0) {
    return null;
  }
  // values.reduce(pick) is unsafe: reduce passes (acc, v, index, array) and Math.min/Math.max treat
  // the extra args as more values, coercing the whole result to NaN.
  const value = values.reduce((acc, v) => pick(acc, v));
  const count = values.filter((v) => v === value).length;
  return { value, count };
}

// Weighted %s are Σnumerator/Σdenominator, not a simple mean — a naive rebuild drifts (33.3% vs the correct 34%).
export function computeOtaSummaryStats(
  parsed: ParsedOtaCsv,
  marketStats: Array<DailyMarketStats>,
  discountStats: Array<DailyDiscountStats>,
  nights: number,
): OtaSummaryStats {
  const allQuotes = allOtaQuoteValues(parsed);
  const avgTotalOtaRate = mean(allQuotes);
  const avgNightlyOtaRate = avgTotalOtaRate != null && nights > 0 ? avgTotalOtaRate / nights : null;

  let genDiscountNumerator = 0;
  let genDiscountDenominator = 0;
  let genDiscountRows = 0;
  let sellVsMinNumerator = 0;
  let sellVsMinDenominator = 0;
  let sellVsMinRows = 0;

  for (const d of discountStats) {
    if (d.prvPlusMin != null && d.prvPlusMin > 0 && d.sellDisplay != null && d.sellDisplay > 0) {
      genDiscountNumerator += d.prvPlusMin - d.sellDisplay;
      genDiscountDenominator += d.prvPlusMin;
      genDiscountRows += 1;
    }
    if (d.low != null && d.low > 0) {
      sellVsMinNumerator += d.low - (d.sellDisplay ?? 0);
      sellVsMinDenominator += d.low;
      sellVsMinRows += 1;
    }
  }

  const genDiscountValues = discountStats.map((d) => d.genDiscountPct).filter((v): v is number => v != null);
  const sellVsMinValues = discountStats.map((d) => d.sellVsMinOtaPct).filter((v): v is number => v != null);

  return {
    datesCount: marketStats.length,
    otaChannelCount: parsed.otaColumns.length,
    avgNightlyOtaRate,
    avgTotalOtaRate,
    weightedGenDiscountPct: {
      valuePct: genDiscountDenominator > 0 ? (genDiscountNumerator / genDiscountDenominator) * 100 : null,
      sumNumerator: genDiscountNumerator,
      sumDenominator: genDiscountDenominator,
      rowsIncluded: genDiscountRows,
    },
    weightedSellVsMinOtaPct: {
      valuePct: sellVsMinDenominator > 0 ? (sellVsMinNumerator / sellVsMinDenominator) * 100 : null,
      sumNumerator: sellVsMinNumerator,
      sumDenominator: sellVsMinDenominator,
      rowsIncluded: sellVsMinRows,
    },
    genDiscountQuartiles: quartilesOf(genDiscountValues),
    sellVsMinOtaQuartiles: quartilesOf(sellVsMinValues),
    lowestOtaQuote: extremeWithCount(allQuotes, Math.min),
    highestOtaQuote: extremeWithCount(allQuotes, Math.max),
  };
}
